import React, { useContext, useEffect, useState } from "react";
import axios from "../api/axios";
import { PreferencesContext } from "../context/PreferencesContext";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  IconButton,
} from "@mui/material";
import { Edit, Delete, Add, History } from "@mui/icons-material";
import CommonTable from "../components/CommonTable";
import { useConfirm } from "../context/ConfirmContext";
import { useMessage } from "../context/MessageContext";
import ViewFulfillmentsDialog from "../components/ViewFulfillmentsDialog";
import {
  buildCatalogItemOptions,
  displayText,
  resolveCatalogLineLabel,
} from "../utils/catalogOptionUtils";

const StockRequests = () => {
  const { organization, academicYear } = useContext(PreferencesContext);
  const { confirm } = useConfirm();
  const { showMessage } = useMessage();

  const [vendor, setVendor] = useState("");
  const [vendors, setVendors] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    items: [],
    description: "",
    status: "Draft",
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [editingIdx, setEditingIdx] = useState(null);
  const [itemEntry, setItemEntry] = useState(null);
  const [itemsOptions, setItemsOptions] = useState([]);
  const [historyDialog, setHistoryDialog] = useState({
    open: false,
    history: [],
  });

  const [fulfillmentDialog, setFulfillmentDialog] = useState({
    open: false,
    request: null,
    receipts: [],
  });

  // Fetch vendors
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await axios.get(
          `/vendors?orgId=${organization}&academicYear=${academicYear}`
        );
        setVendors(res.data);
        // if (res.data.length && !vendor) setVendor(res.data[0]._id);
      } catch {
        showMessage("Failed to load vendors", "error");
      }
    };
    if (organization && academicYear) fetchVendors();
  }, [organization, academicYear]);

  // Fetch catalog
  useEffect(() => {
    const fetchCatalog = async () => {
      if (!vendor) {
        setCatalog(null);
        return;
      }
      setCatalog(null);
      try {
        const res = await axios.get(
          `/catalogs?vendorId=${vendor}&academicYear=${academicYear}`
        );
        setCatalog(res.data || null);
      } catch {
        showMessage("Failed to load catalog", "error");
        setCatalog(null);
      }
    };
    fetchCatalog();
  }, [vendor, academicYear]);

  // Fetch requests
  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `/procurement/requests?orgId=${organization}&vendor=${vendor}&academicYear=${academicYear}`
      );
      setRequests(res.data || []);
    } catch {
      showMessage("Failed to load requests", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (vendor && organization && academicYear) {
      fetchRequests();
    }
  }, [vendor, organization, academicYear]);

  useEffect(() => {
    setItemsOptions(buildCatalogItemOptions(catalog));
  }, [catalog]);

  useEffect(() => {
    setItemEntry(null);
    setSelectedItems([]);
    setOpen(false);
  }, [vendor]);

  const handleOpen = (req = null, idx = null) => {
    setEditingIdx(idx);
    setForm(req || { items: [], description: "", status: "Draft" });
    setSelectedItems(
      req
        ? req.items.map((it) => {
            const id = displayText(it.itemId, "");
            const opt = itemsOptions.find(
              (o) => o.id === id && o.isCollection === it.isCollection
            );
            return opt
              ? { ...opt, quantity: it.quantity }
              : {
                  id,
                  label: resolveCatalogLineLabel(
                    it.itemId,
                    it.isCollection,
                    itemsOptions
                  ),
                  isCollection: it.isCollection,
                  quantity: it.quantity,
                };
          })
        : []
    );
    setItemEntry(null);
    setOpen(true);
  };

  const handleSave = async () => {
    if (!selectedItems.length)
      return showMessage("Add at least one item/collection", "warning");
    // if (!form?.description)
    //   return showMessage("Add request reference", "warning");
    const payload = selectedItems.map(({ id, isCollection, quantity }) => ({
      itemId: id,
      isCollection,
      quantity,
    }));
    const data = {
      ...form,
      items: payload,
      organization,
      vendor,
      academicYear,
      catalog: catalog?._id,
    };
    try {
      if (editingIdx !== null) {
        await axios.put(
          `/procurement/requests/${requests[editingIdx]._id}`,
          data
        );
      } else {
        await axios.post(`/procurement/requests`, data);
      }
      setOpen(false);
      fetchRequests();
      showMessage("Request saved", "success");
    } catch {
      showMessage("Failed to save request", "error");
    }
  };

  const handleDelete = async (id) => {
    if (await confirm({ title: "Confirm", message: "Delete this request?" })) {
      try {
        await axios.delete(`/procurement/requests/${id}`);
        fetchRequests();
        showMessage("Request deleted", "success");
      } catch {
        showMessage("Failed to delete request", "error");
      }
    }
  };

  const handleStatusChange = async (id, nextStatus) => {
    let msg = "Are you sure?";
    if (nextStatus === "Placed") {
      msg += " Make sure you've communicated this to the vendor.";
    }
    if (!(await confirm({ title: "Confirm", message: msg }))) return;
    try {
      await axios.patch(`/procurement/requests/${id}/status`, {
        status: nextStatus,
      });
      fetchRequests();
      showMessage("Status updated", "success");
    } catch {
      showMessage("Failed to update status", "error");
    }
  };

  const openFulfillmentsDialog = async (req) => {
    try {
      const res = await axios.get(
        `/procurement/requests/${req._id}/fulfillments`
      );
      setFulfillmentDialog({
        open: true,
        request: req,
        receipts: res.data.receipts || [],
        statusChangeHistory: res.data.statusChangeHistory || [],
      });
    } catch {
      showMessage("Failed to load fulfillments", "error");
    }
  };

  const getPendingQtyForItem = (itemId, isCollection) => {
    return requests
      .filter((r) => r.status !== "Fulfilled" && r.status !== "Cancelled")
      .flatMap((r) => r.items || [])
      .filter((it) => it.itemId === itemId && it.isCollection === isCollection)
      .reduce(
        (sum, it) => sum + Math.max(it.quantity - (it.fulfilledQty || 0), 0),
        0
      );
  };

  const getLabel = (item) =>
    resolveCatalogLineLabel(item.itemId, item.isCollection, itemsOptions);

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        Stock Requests
      </Typography>
      <FormControl sx={{ mb: 2, minWidth: 250 }}>
        <InputLabel>Vendor</InputLabel>
        <Select
          value={vendor}
          onChange={(e) => setVendor(e.target.value)}
          label="Vendor"
        >
          {vendors.map((v) => (
            <MenuItem key={v._id} value={v._id}>
              {v.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        variant="contained"
        onClick={() => handleOpen()}
        sx={{ ml: 2, mb: 2 }}
        disabled={!vendor}
      >
        Add Request
      </Button>

      <CommonTable
        rows={requests}
        columns={[
          {
            label: "Status",
            getExportValue: (req) => req.status,
            renderCell: (req) => (
              <>
                <Chip
                  onClick={() => {}}
                  label={req.status}
                  color={
                    req.status === "Draft"
                      ? "default"
                      : req.status === "Placed"
                      ? "info"
                      : req.status === "Partially Fulfilled"
                      ? "warning"
                      : req.status === "Fulfilled"
                      ? "success"
                      : req.status === "default"
                      ? "default"
                      : "default"
                  }
                />
                {req.status === "Draft" && (
                  <Button
                    size="small"
                    onClick={() => handleStatusChange(req._id, "Placed")}
                  >
                    Place
                  </Button>
                )}
                {req.status === "Placed" && (
                  <Button
                    size="small"
                    onClick={() => handleStatusChange(req._id, "Cancelled")}
                  >
                    Cancel
                  </Button>
                )}
              </>
            ),
          },
          { label: "Request ID", field: "requestNumber" },
          { label: "Description", field: "description" },
          {
            label: "Items/Collections (Qty)",
            renderCell: (req) => (
              <Box sx={{ whiteSpace: "pre-line" }}>
                {(req.items || [])
                  .map((it) => {
                    const label = getLabel(it); // from itemsOptions
                    const pending = it.quantity - it.fulfilledQty;
                    return `${label} (${it.quantity}) — Pending: ${pending}`;
                  })
                  .join("\n")}
              </Box>
            ),
            getExportValue: (req) =>
              (req.items || [])
                .map((it) => {
                  const label = resolveCatalogLineLabel(
                    it.itemId,
                    it.isCollection,
                    itemsOptions
                  );
                  return `${label} (${it.quantity})`;
                })
                .join(", "),
          },
          {
            label: "Actions",
            renderCell: (req, i) => (
              <>
                <IconButton
                  size="small"
                  disabled={req.status !== "Draft"}
                  onClick={() => handleOpen(req, i)}
                >
                  <Edit />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={req.status !== "Draft"}
                  onClick={() => handleDelete(req._id)}
                >
                  <Delete />
                </IconButton>

                <IconButton
                  size="small"
                  onClick={() => openFulfillmentsDialog(req)}
                >
                  <History />
                </IconButton>
              </>
            ),
            getExportValue: () => "",
          },
        ]}
        loading={loading}
      />

      {/* Add/Edit dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
        <DialogTitle>
          {editingIdx !== null ? "Edit" : "Add"} Request
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Description (Optional)"
            value={form.description}
            onChange={(e) =>
              setForm((f) => ({ ...f, description: e.target.value }))
            }
            fullWidth
            margin="normal"
          />

          <Typography sx={{ mt: 2, mb: 1 }}>Add Item/Collection</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Autocomplete
              options={itemsOptions.filter(
                (opt) => !selectedItems.some((sel) => sel.id === opt.id)
              )}
              getOptionLabel={(opt) => displayText(opt?.label, "Unknown")}
              value={itemEntry}
              onChange={(e, v) =>
                setItemEntry(v ? { ...v, quantity: 1 } : null)
              }
              renderInput={(params) => <TextField {...params} label="Select" />}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Qty"
              type="number"
              value={itemEntry?.quantity || 1}
              onChange={(e) => {
                const q = Math.max(1, parseInt(e.target.value, 10) || 1);
                setItemEntry((entry) =>
                  entry ? { ...entry, quantity: q } : entry
                );
              }}
              sx={{ width: 90 }}
              disabled={!itemEntry}
            />
            <Button
              variant="contained"
              onClick={() => {
                if (itemEntry) {
                  setSelectedItems([...selectedItems, itemEntry]);
                  setItemEntry(null);
                }
              }}
              disabled={!itemEntry}
            >
              Add
            </Button>
          </Box>
          {itemEntry &&
            getPendingQtyForItem(itemEntry.id, itemEntry.isCollection) > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Already pending in previous requests:{" "}
                <strong>
                  {getPendingQtyForItem(itemEntry.id, itemEntry.isCollection)}{" "}
                  units
                </strong>
              </Typography>
            )}

          <Typography sx={{ mt: 2, mb: 1 }}>Selected Items</Typography>
          {selectedItems.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No items added
            </Typography>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {selectedItems.map((item, idx) => (
              <Box key={item.id} sx={{ display: "flex", alignItems: "center" }}>
                <Chip label={displayText(item.label, "Item")} sx={{ mr: 2 }} />
                <Typography sx={{ mr: 2 }}>Qty: {item.quantity}</Typography>
                <Button
                  size="small"
                  onClick={() =>
                    setSelectedItems((list) => list.filter((_, i) => i !== idx))
                  }
                >
                  Remove
                </Button>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editingIdx !== null ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History dialog */}
      <Dialog
        open={historyDialog.open}
        onClose={() => setHistoryDialog({ open: false, history: [] })}
        fullWidth
      >
        <DialogTitle>Status Change History</DialogTitle>
        <DialogContent>
          {historyDialog.history.length === 0 ? (
            <Typography>No history available</Typography>
          ) : (
            historyDialog.history.map((h, idx) => (
              <Box key={idx} sx={{ mb: 1 }}>
                <Typography variant="body2">
                  {h.oldStatus} ➡ {h.newStatus} on{" "}
                  {new Date(h.updatedAt).toLocaleString()}
                </Typography>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setHistoryDialog({ open: false, history: [] })}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fullfillment dialog */}
      <ViewFulfillmentsDialog
        open={fulfillmentDialog.open}
        onClose={() => setFulfillmentDialog({ open: false })}
        request={fulfillmentDialog.request}
        receipts={fulfillmentDialog.receipts}
        statusChangeHistory={fulfillmentDialog.statusChangeHistory || []}
        itemsOptions={itemsOptions}
      />
    </Box>
  );
};

export default StockRequests;
